import type { components } from '../api/types';
import { webSocket } from 'rxjs/webSocket';
import { LoginStatus, loginStatus$ } from '../store/loginV2';
import { map, switchMap, take, tap } from 'rxjs';

export type NewPresentationSettings =
    components['schemas']['NewPresentationSettings'];

export type NewPersonSettings = components['schemas']['NewPersonSettings'];

export type NewPersonResult = components['schemas']['PersonData'];

interface CreatePresentationMessage {
    type?: undefined;
    action: 'create-presentation';
    payload: NewPresentationSettings;
}

interface CreatePersonMessage {
    type?: undefined;
    action: 'create-person';
    payload: NewPersonSettings;
}

interface CreatedPersonMessage {
    type: 'created-person';
    payload: NewPersonResult;
}

interface ProgressMessage {
    type: 'progress';
    message: string;
}

interface CreatedPresentationMessage {
    type: 'created-presentation';
    url: string;
}

interface AbortMessage {
    type: 'abort';
    message: string;
    flaggingInformation: string;
}

interface ErrorMessage {
    type: 'error';
    message: string;
}
type Message =
    | ProgressMessage
    | CreatePresentationMessage
    | CreatedPresentationMessage
    | CreatePersonMessage
    | CreatedPersonMessage
    | AbortMessage
    | ErrorMessage;

function getWebsocketUrl(token: string) {
    const isSecure = /https/.test(window.location.protocol);
    return `ws${isSecure ? 's' : ''}://${window.location.hostname}:${
        window.location.port
    }/presentation?jwt=${token}`;
}

export function createNewPresentation(settings: NewPresentationSettings) {
    return loginStatus$.pipe(
        take(1),
        map((status) => {
            if (status.status !== LoginStatus.LOGGED_IN) {
                throw new Error('User is not logged in!');
            }
            return status.token;
        }),
        switchMap((token) => {
            const url = getWebsocketUrl(token);

            const socket = webSocket<Message>(url);
            socket.next({
                action: 'create-presentation',
                payload: settings,
            });
            return socket.pipe(
                tap((msg) => {
                    if (msg.type === 'created-presentation') {
                        socket.complete();
                    }
                })
            );
        })
    );
}

export function createNewPerson(settings: NewPersonSettings) {
    return loginStatus$.pipe(
        take(1),
        map((status) => {
            if (status.status !== LoginStatus.LOGGED_IN) {
                throw new Error('User is not logged in!');
            }
            return status.token;
        }),
        switchMap((token) => {
            const url = getWebsocketUrl(token);
            const socket = webSocket<Message>(url);
            socket.next({
                action: 'create-person',
                payload: settings,
            });
            return socket.pipe(
                tap((msg) => {
                    if (msg.type === 'created-person') {
                        socket.complete();
                    }
                })
            );
        })
    );
}
